/**
 * The note under Pilot mode when a person picks Autopilot for a job still in Calibration (owner, 2026-10-31):
 * "Calibration is recommended to complete before using Autopilot." Shown inline, never a dialog, once per job on
 * this computer: the jobs it has shown for are kept in localStorage, read and written as the hints' closed list is
 * (document-hints.ts, HINTS_CLOSED_KEY). A broken or unreadable record shows it again, never stops the choice.
 */
export const AUTOPILOT_CALIBRATION_NOTED_KEY = 'cpv6-autopilot-calibration-noted';
export const AUTOPILOT_CALIBRATION_NOTE =
  'Calibration is recommended to complete before using Autopilot.';

type NoteStorage = Pick<Storage, 'getItem' | 'setItem'>;

/** The jobs the note has shown for on this computer. */
export function readAutopilotCalibrationNoted(
  storage: Pick<Storage, 'getItem'> | undefined = globalThis.localStorage,
): string[] {
  try {
    const value = JSON.parse(storage?.getItem(AUTOPILOT_CALIBRATION_NOTED_KEY) ?? '[]');
    return Array.isArray(value)
      ? [...new Set(value.filter((id): id is string => typeof id === 'string'))]
      : [];
  } catch {
    return []; /* A broken record notes nothing: the note may show again. */
  }
}

/**
 * Whether picking Autopilot shows the note now, and records it when it does: the job is calibrating, is not a
 * sample job, and the note has not shown for it on this computer.
 */
export function takeAutopilotCalibrationNote(
  { jobId, calibrating, sample }: { jobId: string; calibrating: boolean; sample: boolean },
  storage: NoteStorage | undefined = globalThis.localStorage,
): boolean {
  if (!calibrating || sample) return false;
  const noted = readAutopilotCalibrationNoted(storage);
  if (noted.includes(jobId)) return false;
  try {
    storage?.setItem(AUTOPILOT_CALIBRATION_NOTED_KEY, JSON.stringify([...noted, jobId]));
  } catch {
    /* It may show again. */
  }
  return true;
}
